import React, { useEffect, useState } from 'react';
import { SystemCard } from '../../component/System';
import CardGallery from '../../component/CardGallery';
import { useSearchParams } from 'react-router-dom';
import { FilterOption } from '../../component/FilterBar';
import FilterBar from '../../component/FilterBar';
import Loading from '../../component/Loading';

const SystemManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [systems, setSystems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit') || '20', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterLoading, setFilterLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sort_by') || 'name');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>( (searchParams.get('sort_order') as any) || 'asc' );
  const [systemNames, setSystemNames] = useState<string[]>([]);

  const [filters, setFilters] = useState({
    name: getArrayParamFromUrl('name'),
    status: getArrayParamFromUrl('status'),
  });

  function getArrayParamFromUrl(paramName: string): string[] {
    const params = searchParams.getAll(paramName);
    return params.length > 0 ? params : [];
  }

  useEffect(() => {
    fetchSystems(currentPage, pageSize, filters);
    fetchSystemNames();
  }, [currentPage, pageSize]);

  const fetchSystemNames = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/system_names`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch system names');
      }
      const data = await response.json();
      setSystemNames(data);
    } catch (error) {
      console.error('Error fetching system names:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchSystems(1, pageSize, filters);
  };

  async function fetchSystems(page: number, size: number, filterValues: Record<string, any> = {}) {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      Object.entries(filterValues).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(val => queryParams.append(key, val));
        } else if (value) {
          queryParams.set(key, value);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/systems?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch systems data.');
      }
      const data = await response.json();
      setSystems(data.items);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_count);
    } catch (error) {
      console.error('Error fetching systems data:', error);
    } finally {
      setLoading(false);
    }
  }

  const filterBarOptions: FilterOption[] = [
    {
      name: "name",
      label: "System Name",
      type: "select",
      options: systemNames, // Populated from API call
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: ['Online', 'Offline'],
    },
  ];

  const systemSortOptions = [
    { value: "name", label: "System Name" },
    { value: "status", label: "Status" },
    { value: "createdAt", label: "Created At" },
    { value: "updatedAt", label: "Updated At" },
  ];

  return (
    <div className="flex h-screen">
      <main className="flex-1 bg-gray-500 p-6">
        {loading && <Loading message="Loading Systems..." />}
        <FilterBar
          filters={filters}
          filterOptions={filterBarOptions}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(newSortBy, newSortOrder) => {
            setSortBy(newSortBy);
            setSortOrder(newSortOrder);
            fetchSystems(currentPage, pageSize, filters);
          }}
          isLoading={filterLoading}
          sortOptions={systemSortOptions}
        />
        <CardGallery
          CardComponent={SystemCard}
          cardClickPath="/system-management"
          data={systems}
          loading={loading}
          onPageChange={(page: number, size?: number) => {
            setCurrentPage(page);
            if (size && size !== pageSize) {
              setPageSize(size);
            }
          }}
          itemsPerPage={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          currentPage={currentPage}
        />
      </main>
    </div>
  );
};

export default SystemManagementPage;